import { db } from "../firebase/config";
import {
  collection,
  doc,
  updateDoc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  limit,
  Timestamp,
  writeBatch,
  CollectionReference,
  Unsubscribe,
} from "firebase/firestore";
import FirebaseFunctionProxy from "../utils/FirebaseFunctionProxy";
import { getAuth } from "firebase/auth";

type JobStatus = "queued" | "in-progress" | "completed" | "error";
export type PullRequestMergeStatus = "merged" | "failed";
export type PullRequestMergeMethod = "merge" | "squash" | "rebase";

export interface TranslationJob {
  jobId: string;
  owner: string;
  repo: string;
  sourcePath: string;
  locale: string;
  status: JobStatus;
  progress: number;
  createdAt: Date;
  updatedAt: Date;
  totalChunks: number;
  completedChunks: number;
  createPR: boolean;
  userId: string;
  requestId?: string;
  prNumber?: number;
  prUrl?: string;
  branch?: string;
  prError?: string;
  prMergeStatus?: PullRequestMergeStatus;
  prMergeMethod?: PullRequestMergeMethod;
  prMergeError?: string;
  prMergedAt?: Date;
}

export interface TranslationChunk {
  id: string;
  jobId: string;
  status: "pending" | "processing" | "completed" | "failed";
  keyPath: string;
  content: any;
  result?: any;
  error?: string;
  createdAt: Date;
  updatedAt: Date;
  processingStartedAt?: Date;
}

export interface TranslationJobDetails {
  job: TranslationJob;
  chunks: TranslationChunk[];
  translatedContent: any | null;
}

// Validation errors that can occur
export interface ValidationError {
  path: string;
  message: string;
  value?: any;
}

/**
 * Service for managing translation tasks
 */
export class TranslationService {
  /**
   * Split a large object into smaller chunks for processing
   * Each top-level key becomes a separate chunk
   */
  static splitIntoChunks(
    sourceObject: any
  ): { keyPath: string; content: any }[] {
    const chunks: { keyPath: string; content: any }[] = [];

    // Handle top-level keys as chunks
    for (const key in sourceObject) {
      chunks.push({
        keyPath: key,
        content: { [key]: sourceObject[key] },
      });
    }

    return chunks;
  }

  /**
   * Validate translation content before submitting
   * @param content Content to validate
   * @returns Boolean indicating if content is valid
   */
  static validateTranslationContent(content: any): boolean {
    if (!content) return false;

    try {
      // If it's a string, try to parse it as JSON
      if (typeof content === "string") {
        JSON.parse(content);
      } else if (typeof content !== "object") {
        return false;
      }

      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Deep validate translation object structure and values
   * @param content Translation content to validate in depth
   * @returns Array of validation errors, empty if valid
   */
  static deepValidateTranslation(content: any): ValidationError[] {
    const errors: ValidationError[] = [];

    // Helper function to validate object structure recursively
    const validateObject = (obj: any, path = "") => {
      if (!obj || typeof obj !== "object") {
        errors.push({
          path,
          message: `Expected an object at ${path || "root"}`,
          value: obj,
        });
        return;
      }

      for (const key in obj) {
        const currentPath = path ? `${path}.${key}` : key;
        const value = obj[key];

        // Check null values
        if (value === null) {
          errors.push({
            path: currentPath,
            message: "Null values are not allowed",
            value: null,
          });
          continue;
        }

        // For nested objects, recurse
        if (typeof value === "object" && !Array.isArray(value)) {
          validateObject(value, currentPath);
        } else if (Array.isArray(value)) {
          // Validate arrays - ensure no null values
          value.forEach((item, index) => {
            if (item === null) {
              errors.push({
                path: `${currentPath}[${index}]`,
                message: "Array should not contain null values",
                value: null,
              });
            } else if (typeof item === "object") {
              validateObject(item, `${currentPath}[${index}]`);
            }
          });
        }
        // Other types like strings, numbers, booleans are valid
      }
    };

    validateObject(content);
    return errors;
  }

  /**
   * Creates a translation job plus all chunk docs in one batch.
   * Returns the new `jobId`.
   */
  static async createTranslationJob(
    owner: string,
    repo: string,
    sourcePath: string,
    locale: string,
    content: unknown,
    userId: string,
    createPR = true,
    requestId?: string
  ): Promise<string> {
    /* 1 ─ validate --------------------------------------------------- */
    if (!TranslationService.validateTranslationContent(content))
      throw new Error("Invalid translation content.");

    const errors = TranslationService.deepValidateTranslation(content);
    if (errors.length)
      throw new Error(
        `Translation validation failed with ${errors.length} error(s).`
      );

    /* 2 ─ split into chunks ----------------------------------------- */
    const chunks = TranslationService.splitIntoChunks(content);
    const totalChunks = chunks.length;

    /* 3 ─ refs ------------------------------------------------------- */
    const userRef = doc(db, "users", userId); // 🔸 FIX #1
    const jobColl = collection(
      userRef,
      "translationJobs"
    ) as CollectionReference<TranslationJob>;
    const jobRef = doc(jobColl);
    const jobId = jobRef.id;

    const repoRef = doc(db, "translationQueue", repo); // 🔸 FIX #2
    const chunksCol = collection(repoRef, "chunks");

    /* 4 ─ batch write ---------------------------------------------- */
    const batch = writeBatch(db);

    // 4-a materialise /users/<userId>
    batch.set(userRef, { lastJobAt: serverTimestamp() }, { merge: true });

    // 4-b materialise /translationQueue/<repo>
    batch.set(repoRef, { lastJobAt: serverTimestamp() }, { merge: true });

    // 4-c job header
    batch.set(jobRef, {
      jobId,
      owner,
      repo,
      sourcePath,
      locale,
      status: "queued",
      progress: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      totalChunks,
      completedChunks: 0,
      createPR,
      userId,
      ...(requestId ? { requestId } : {}),
    });

    // 4-d chunk docs
    chunks.forEach((chunk, idx) => {
      const chunkRef = doc(chunksCol);
      batch.set(chunkRef, {
        jobId,
        owner,
        repo,
        locale,
        status: "pending",
        keyPath: chunk.keyPath,
        content: chunk.content,
        priority: idx,
        createPR,
        userId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    await batch.commit();
    return jobId;
  }

  /**
   * Process a chunk from the translation queue
   */

  /**
   * Check if a job is completed and create a PR if needed
   */
  static async checkJobCompletion(
    jobId: string,
    userId: string,
    repoName: string
  ): Promise<void> {
    const jobRef = doc(db, "users", userId, "translationJobs", jobId);
    const jobDoc = await getDoc(jobRef);

    if (!jobDoc.exists()) {
      throw new Error(`Job ${jobId} not found`);
    }

    const job = jobDoc.data();

    // Calculate progress from chunks
    const queueQuery = query(
      collection(db, "translationQueue", repoName, "chunks"),
      where("jobId", "==", jobId)
    );
    const queueDocs = await getDocs(queueQuery);

    const totalChunks = queueDocs.size;
    const completedChunks = queueDocs.docs.filter(
      (doc) => doc.data().status === "completed"
    ).length;
    const failedChunks = queueDocs.docs.filter(
      (doc) => doc.data().status === "failed"
    ).length;

    // If all chunks are processed
    if (completedChunks + failedChunks === totalChunks) {
      // If any chunk failed, mark job as failed
      if (failedChunks > 0) {
        await updateDoc(jobRef, {
          status: "failed",
          progress: (completedChunks / totalChunks) * 100,
          completedChunks,
          updatedAt: serverTimestamp(),
          error: `${failedChunks} chunks failed processing`,
        });
      } else {
        // All chunks completed successfully
        await updateDoc(jobRef, {
          status: "completed",
          progress: 100,
          completedChunks,
          updatedAt: serverTimestamp(),
        });

        // Create PR if requested
        if (job.createPR) {
          try {
            // This would call the GitHub service to create a PR
            // const prUrl = await GitHubService.createPR(job);
            const prUrl = `https://github.com/${job.owner}/${job.repo}/pull/123`;

            await updateDoc(jobRef, {
              prUrl,
              updatedAt: serverTimestamp(),
            });

            // Send completion email notification with PR info
            await this.sendCompletionEmail({
              jobId,
              owner: job.owner,
              repo: job.repo,
              locale: job.locale,
              userId,
              sourcePath: job.sourcePath,
              prUrl,
            });
          } catch (error: any) {
            console.error(`Error creating PR for job ${jobId}:`, error);

            // Mark job as completed but PR failed
            await updateDoc(jobRef, {
              prError: error.message,
              updatedAt: serverTimestamp(),
            });

            // Still send completion email, but without PR info
            await this.sendCompletionEmail({
              jobId,
              owner: job.owner,
              repo: job.repo,
              locale: job.locale,
              userId,
              sourcePath: job.sourcePath,
            });
          }
        } else {
          // Send completion email without PR info
          await this.sendCompletionEmail({
            jobId,
            owner: job.owner,
            repo: job.repo,
            locale: job.locale,
            userId,
            sourcePath: job.sourcePath,
          });
        }
      }
    }
  }

  /**
   * Send email notification when a translation job completes
   */
  static async sendCompletionEmail(params: {
    jobId: string;
    owner: string;
    repo: string;
    locale: string;
    userId: string;
    sourcePath: string;
    prUrl?: string;
  }): Promise<void> {
    try {
      const auth = getAuth();
      if (!auth.currentUser) {
        console.error("User must be authenticated to send email notifications");
        return;
      }

      // Get user's email address
      const userEmail = auth.currentUser.email;
      if (!userEmail) {
        console.error("User email not available");
        return;
      }

      // Call the Firebase function to send the email
      await FirebaseFunctionProxy.callFunction(
        "sendTranslationCompletedEmail",
        {
          ...params,
          email: userEmail,
        }
      );

      console.log(`Translation completion email sent for job ${params.jobId}`);
    } catch (error: any) {
      console.error(
        `Error sending translation completion email for job ${params.jobId}:`,
        error
      );
      // Don't throw here - we don't want to fail the job completion
      // just because the email notification failed
    }
  }

  /**
   * Retrieve the next pending chunk for processing
   */
  static async getNextPendingChunk(
    repoName: string
  ): Promise<TranslationChunk | null> {
    const chunkQuery = query(
      collection(db, "translationQueue", repoName, "chunks"),
      where("status", "==", "pending"),
      orderBy("priority"),
      limit(1)
    );

    const chunkDocs = await getDocs(chunkQuery);

    if (chunkDocs.empty) {
      return null;
    }

    const chunkDoc = chunkDocs.docs[0];
    const data = chunkDoc.data();

    return {
      id: chunkDoc.id,
      jobId: data.jobId,
      status: data.status,
      keyPath: data.keyPath,
      content: data.content,
      createdAt: (data.createdAt as Timestamp).toDate(),
      updatedAt: (data.updatedAt as Timestamp).toDate(),
    };
  }

  /**
   * Get jobs for a user
   */
  static async getUserJobs(userId: string): Promise<TranslationJob[]> {
    const jobsQuery = query(
      collection(db, "users", userId, "translationJobs"),
      orderBy("createdAt", "desc")
    );

    const jobDocs = await getDocs(jobsQuery);
    const jobs: TranslationJob[] = [];

    jobDocs.forEach((doc) => {
      const data = doc.data();
      jobs.push({
        jobId: doc.id,
        repo: data.repo,
        owner: data.owner,
        locale: data.locale,
        status: data.status,
        progress: data.progress,
        createdAt: (data.createdAt as Timestamp).toDate(),
        updatedAt: (data.updatedAt as Timestamp).toDate(),
        totalChunks: data.totalChunks,
        completedChunks: data.completedChunks,
        sourcePath: data.sourcePath,
        createPR: data.createPR,
        userId: data.userId,
        requestId: data.requestId,
        prNumber:
          typeof data.prNumber === "number"
            ? data.prNumber
            : Number.isFinite(Number(data.prNumber))
            ? Number(data.prNumber)
            : undefined,
        prUrl: data.prUrl,
        branch: data.branch,
        prError: data.prError,
        prMergeStatus: data.prMergeStatus,
        prMergeMethod: data.prMergeMethod,
        prMergeError: data.prMergeError,
        prMergedAt: (data.prMergedAt as Timestamp)?.toDate(),
      });
    });

    return jobs;
  }

  static async getJob(userId: string, jobId: string): Promise<TranslationJob> {
    const jobRef = doc(db, "users", userId, "translationJobs", jobId);
    const jobDoc = await getDoc(jobRef);

    if (!jobDoc.exists()) {
      throw new Error(`Job ${jobId} not found`);
    }

    const data = jobDoc.data();

    return {
      jobId: jobDoc.id,
      repo: data.repo,
      owner: data.owner,
      locale: data.locale,
      status: data.status,
      progress: data.progress,
      createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
      updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
      totalChunks: data.totalChunks,
      completedChunks: data.completedChunks,
      sourcePath: data.sourcePath,
      createPR: data.createPR,
      userId: data.userId,
      requestId: data.requestId,
      prNumber:
        typeof data.prNumber === "number"
          ? data.prNumber
          : Number.isFinite(Number(data.prNumber))
          ? Number(data.prNumber)
          : undefined,
      prUrl: data.prUrl,
      branch: data.branch,
      prError: data.prError,
      prMergeStatus: data.prMergeStatus,
      prMergeMethod: data.prMergeMethod,
      prMergeError: data.prMergeError,
      prMergedAt: (data.prMergedAt as Timestamp)?.toDate(),
    };
  }

  static async getJobChunks(
    repoName: string,
    jobId: string
  ): Promise<TranslationChunk[]> {
    const chunkQuery = query(
      collection(db, "translationQueue", repoName, "chunks"),
      where("jobId", "==", jobId),
      orderBy("priority")
    );

    const chunkDocs = await getDocs(chunkQuery);

    return chunkDocs.docs.map((chunkDoc) => {
      const data = chunkDoc.data();

      return {
        id: chunkDoc.id,
        jobId: data.jobId,
        status: data.status,
        keyPath: data.keyPath,
        content: data.content,
        result: data.result,
        error: data.error,
        createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
        updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
        processingStartedAt: (data.processingStartedAt as Timestamp)?.toDate(),
      };
    });
  }

  static async getJobDetails(
    userId: string,
    jobId: string
  ): Promise<TranslationJobDetails> {
    const job = await this.getJob(userId, jobId);
    const [chunks, translatedContent] = await Promise.all([
      this.getJobChunks(job.repo, jobId),
      this.assembleTranslation(jobId, job.repo),
    ]);

    return {
      job,
      chunks,
      translatedContent:
        translatedContent && Object.keys(translatedContent).length > 0
          ? translatedContent
          : null,
    };
  }

  /**
   * Subscribe to user jobs for real-time updates
   */
  static subscribeToUserJobs(
    userId: string,
    callback: (jobs: TranslationJob[]) => void
  ): Unsubscribe {
    const jobsQuery = query(
      collection(db, "users", userId, "translationJobs"),
      orderBy("createdAt", "desc")
    );

    return onSnapshot(jobsQuery, (snapshot) => {
      const jobs: TranslationJob[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        jobs.push({
          jobId: doc.id,
          repo: data.repo,
          owner: data.owner,
          locale: data.locale,
          status: data.status,
          progress: data.progress,
          createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
          updatedAt: (data.updatedAt as Timestamp)?.toDate() || new Date(),
          totalChunks: data.totalChunks,
          completedChunks: data.completedChunks,
          sourcePath: data.sourcePath,
          createPR: data.createPR,
          userId: data.userId,
          requestId: data.requestId,
          prNumber:
            typeof data.prNumber === "number"
              ? data.prNumber
              : Number.isFinite(Number(data.prNumber))
              ? Number(data.prNumber)
              : undefined,
          prUrl: data.prUrl,
          branch: data.branch,
          prError: data.prError,
          prMergeStatus: data.prMergeStatus,
          prMergeMethod: data.prMergeMethod,
          prMergeError: data.prMergeError,
          prMergedAt: (data.prMergedAt as Timestamp)?.toDate(),
        });
      });
      callback(jobs);
    });
  }

  static async markJobPullRequestMerged(
    userId: string,
    jobId: string,
    method: PullRequestMergeMethod
  ): Promise<void> {
    const jobRef = doc(db, "users", userId, "translationJobs", jobId);
    await updateDoc(jobRef, {
      prMergeStatus: "merged",
      prMergeMethod: method,
      prMergeError: null,
      prMergedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }

  static async markJobPullRequestMergeFailed(
    userId: string,
    jobId: string,
    errorMessage: string,
    method: PullRequestMergeMethod
  ): Promise<void> {
    const jobRef = doc(db, "users", userId, "translationJobs", jobId);
    await updateDoc(jobRef, {
      prMergeStatus: "failed",
      prMergeMethod: method,
      prMergeError: errorMessage,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Assemble translated content from chunks
   */
  static async assembleTranslation(
    jobId: string,
    repoName: string
  ): Promise<any> {
    const resultsQuery = query(
      collection(db, "translationQueue", repoName, "results"),
      where("jobId", "==", jobId)
    );

    const resultDocs = await getDocs(resultsQuery);
    const translatedContent: any = {};

    resultDocs.forEach((doc) => {
      const data = doc.data();
      const result = data.result;

      // Merge results
      Object.assign(translatedContent, result);
    });

    return translatedContent;
  }

  /**
   * Try to parse a string as JSON
   * @param jsonString String to parse
   * @returns Parsed object or null if invalid
   */
  static parseJsonSafely(jsonString: string): any | null {
    try {
      return JSON.parse(jsonString);
    } catch (error) {
      return null;
    }
  }

  /**
   * Format validation errors into a readable string
   * @param errors Array of validation errors
   * @returns Formatted error message
   */
  static formatValidationErrors(errors: ValidationError[]): string {
    if (errors.length === 0) return "No errors";

    return errors
      .map((err) => `Path: ${err.path || "root"} - ${err.message}`)
      .join("\n");
  }
}

export default TranslationService;
